/**
 * Reading direction helpers for EPUB (page progression).
 * Source of truth: metadata.json `read_direction`, e.g. "ltr (left to right)".
 */

export const READING_DIRECTION_LTR = "ltr";
export const READING_DIRECTION_RTL = "rtl";

/**
 * Normalize API / bibliographic read_direction strings to "ltr" | "rtl".
 * Accepts: "rtl", "ltr", "rtl (right to left)", "left to right", etc.
 */
export const parseReadDirection = (value) => {
  if (value == null) return READING_DIRECTION_LTR;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return READING_DIRECTION_LTR;

  if (
    normalized === READING_DIRECTION_RTL ||
    normalized.startsWith("rtl") ||
    normalized.includes("right to left") ||
    normalized.includes("right-to-left")
  ) {
    return READING_DIRECTION_RTL;
  }

  return READING_DIRECTION_LTR;
};

/**
 * Resolve read_direction from the various shapes of metadata.json we store.
 */
export const extractReadDirectionFromMetadata = (metadata) => {
  if (!metadata) return null;

  const candidates = [
    metadata.read_direction,
    metadata.metadata?.read_direction,
    metadata.metadata?.bibliographic_data?.read_direction,
    metadata.bibliographic_data?.read_direction,
    metadata.rich_metadata?.read_direction,
    metadata.rich_metadata?.metadata?.read_direction,
    metadata.rich_metadata?.metadata?.bibliographic_data?.read_direction,
  ];

  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== "") {
      return candidate;
    }
  }

  return null;
};

export const getReadingDirectionFromMetadata = (metadata) =>
  parseReadDirection(extractReadDirectionFromMetadata(metadata));

const normalizeDir = (direction) =>
  direction === READING_DIRECTION_RTL
    ? READING_DIRECTION_RTL
    : READING_DIRECTION_LTR;

/**
 * epub.js rendition.direction() updates rendition/manager settings and
 * next/prev scrolling, but does NOT update Layout.settings.direction.
 * Column formatting reads Layout.settings.direction — so without this sync,
 * the UI stays LTR while page math flips to RTL (e.g. 11-13 → 24-26).
 */
export const syncEpubLayoutDirection = (rendition, direction) => {
  if (!rendition) return;
  const dir = normalizeDir(direction);

  if (rendition.settings) {
    rendition.settings.direction = dir;
    if (rendition.settings.globalLayoutProperties) {
      rendition.settings.globalLayoutProperties.direction = dir;
    }
  }

  if (rendition._layout?.settings) {
    rendition._layout.settings.direction = dir;
  }

  if (rendition.manager?.layout?.settings) {
    rendition.manager.layout.settings.direction = dir;
  }

  if (rendition.manager?.settings) {
    rendition.manager.settings.direction = dir;
  }

  if (rendition.manager?.viewSettings) {
    rendition.manager.viewSettings.direction = dir;
  }

  // contents.columns() only sets documentElement direction for rtl, never ltr.
  try {
    const contentsList =
      typeof rendition.getContents === "function" ? rendition.getContents() : [];
    (contentsList || []).forEach((contents) => {
      if (contents && typeof contents.direction === "function") {
        contents.direction(dir);
      }
    });
  } catch (_) {
    // ignore
  }
};

const getRenditionCfi = (rendition) => {
  if (!rendition) return null;
  if (rendition.location?.start?.cfi) return rendition.location.start.cfi;
  try {
    if (typeof rendition.currentLocation === "function") {
      return rendition.currentLocation()?.start?.cfi || null;
    }
  } catch (_) {
    // ignore
  }
  return null;
};

/**
 * Force epub.js page-progression direction on a Book (+ optional Rendition).
 * Set package metadata before renderTo; pass rendition to refresh a live view.
 *
 * @returns {Promise<{ dir: string, changed: boolean }>}
 */
export const applyEpubReadingDirection = async (
  book,
  direction,
  rendition = null,
  options = {},
) => {
  const dir = normalizeDir(direction);
  const force = Boolean(options.force);

  try {
    if (book?.package?.metadata) {
      book.package.metadata.direction = dir;
    }
    if (book?.packaging?.metadata) {
      book.packaging.metadata.direction = dir;
    }
  } catch (_) {
    // ignore metadata write failures
  }

  if (!rendition) {
    return { dir, changed: false };
  }

  const previous = normalizeDir(rendition.settings?.direction);
  const layoutPrevious = normalizeDir(
    rendition._layout?.settings?.direction || previous,
  );
  const changed = force || previous !== dir || layoutPrevious !== dir;

  // Always keep Layout in sync, even when not forcing a redraw.
  syncEpubLayoutDirection(rendition, dir);

  if (!changed) {
    return { dir, changed: false };
  }

  try {
    if (rendition.manager && typeof rendition.manager.direction === "function") {
      rendition.manager.direction(dir);
    }

    const cfi = options.cfi || getRenditionCfi(rendition);
    if (
      rendition.manager?.isRendered?.() &&
      cfi &&
      typeof rendition.display === "function"
    ) {
      // Clear + display so columns are reformatted with Layout.settings.direction.
      rendition.manager.clear();
      await rendition.display(cfi);
    } else if (typeof rendition.direction === "function") {
      rendition.direction(dir);
    }

    syncEpubLayoutDirection(rendition, dir);
  } catch (err) {
    console.warn("Failed to apply EPUB reading direction:", err);
  }

  return { dir, changed: true };
};
